import React, { useMemo } from 'react';
import { InvoiceHeader } from '../services/invoiceApi';
import { Branch } from '../services/branchApi';
import { Customer } from '../services/customerApi';
import { formatDate, formatDateTime } from '../utils/dateUtils';

function toDisplayRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}

function toEffectiveRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}

function formatEwayWhen(iso?: string | null): string {
  if (!iso) return '—';
  const dt = formatDateTime(iso);
  if (dt) return dt;
  return formatDate(iso);
}

/** Map common e-way transport mode codes to labels */
export function formatTransportMode(mode?: string | null, vehicleType?: string | null): string {
  const m = (mode ?? '').trim();
  if (!m && !vehicleType) return '—';
  const map: Record<string, string> = {
    '1': '1 - Road',
    '2': '2 - Rail',
    '3': '3 - Air',
    '4': '4 - Ship',
  };
  if (map[m]) return map[m];
  if (m) return vehicleType ? `${m} (${vehicleType})` : m;
  return vehicleType ?? '—';
}

export interface InvoiceEwayBillLayoutProps {
  invoice: InvoiceHeader;
  branchDetails: Branch | null;
  shipToCustomer: Customer | null;
  /** Extra classes on the outer document wrapper (e.g. print page break) */
  className?: string;
}

/**
 * Printable e-Way Bill document (no QR). Parent should only render when
 * invoice is e-invoiced and has e-Way Bill number.
 */
export const InvoiceEwayBillLayout: React.FC<InvoiceEwayBillLayoutProps> = ({
  invoice,
  branchDetails,
  shipToCustomer,
  className = '',
}) => {
  const isGst = useMemo(() => {
    if (!branchDetails || !shipToCustomer) return true;
    return (branchDetails.stateCode ?? '') !== (shipToCustomer.stateCode ?? '');
  }, [branchDetails, shipToCustomer]);

  const { lineRows, goodsTotals } = useMemo(() => {
    if (!invoice?.invoiceDetails) {
      return {
        lineRows: [] as Array<{
          hsn: string;
          desc: string;
          qty: number;
          uom: string;
          taxable: number;
          taxRateLabel: string;
        }>,
        goodsTotals: null,
      };
    }
    const details = invoice.invoiceDetails;
    const sumLineTaxFields = details.reduce((s, d) => s + (d.cgst ?? 0) + (d.sgst ?? 0) + (d.igst ?? 0), 0);
    const headerTax =
      invoice.grandTotalTax != null && !Number.isNaN(Number(invoice.grandTotalTax))
        ? Number(invoice.grandTotalTax)
        : 0;
    let taxFieldsAreAmounts = false;
    if (headerTax > 0) {
      const diff = Math.abs(sumLineTaxFields - headerTax);
      if (diff <= 0.01 * Math.max(headerTax, 1)) taxFieldsAreAmounts = true;
    } else {
      taxFieldsAreAmounts = details.some(
        (d) => (d.cgst ?? 0) > 100 || (d.sgst ?? 0) > 100 || (d.igst ?? 0) > 100
      );
    }

    let totTaxable = 0;
    let totCgst = 0;
    let totSgst = 0;
    let totIgst = 0;

    const lineRows = details.map((d) => {
      const taxable = (d.qty ?? 0) * (d.rate ?? 0);
      totTaxable += taxable;
      let cgstAmt = 0;
      let sgstAmt = 0;
      let igstAmt = 0;
      if (taxFieldsAreAmounts) {
        cgstAmt = d.cgst ?? 0;
        sgstAmt = d.sgst ?? 0;
        igstAmt = d.igst ?? 0;
      } else if (isGst) {
        igstAmt = (taxable * toEffectiveRate(d.igst ?? 0)) / 100;
      } else {
        cgstAmt = (taxable * toEffectiveRate(d.cgst ?? 0)) / 100;
        sgstAmt = (taxable * toEffectiveRate(d.sgst ?? 0)) / 100;
      }
      totCgst += cgstAmt;
      totSgst += sgstAmt;
      totIgst += igstAmt;

      // Tax rate display:
      // - If line tax fields are rates: show those rates
      // - If line tax fields are amounts: derive % from tax amount / taxable
      const safeTaxable = taxable > 0 ? taxable : 0;
      const cgstR = taxFieldsAreAmounts
        ? (safeTaxable > 0 ? (cgstAmt / safeTaxable) * 100 : 0)
        : toDisplayRate(d.cgst ?? 0);
      const sgstR = taxFieldsAreAmounts
        ? (safeTaxable > 0 ? (sgstAmt / safeTaxable) * 100 : 0)
        : toDisplayRate(d.sgst ?? 0);
      const igstR = taxFieldsAreAmounts
        ? (safeTaxable > 0 ? (igstAmt / safeTaxable) * 100 : 0)
        : toDisplayRate(d.igst ?? 0);
      const taxRateLabel = isGst
        ? `${igstR.toFixed(0)}%`
        : `${cgstR.toFixed(0)}+${sgstR.toFixed(0)}`;

      return {
        hsn: d.hsn ?? '—',
        desc: d.endgoodName ?? '—',
        qty: d.qty ?? 0,
        uom: d.uom ?? 'NOS',
        taxable,
        taxRateLabel,
      };
    });

    const otherAmt = (invoice.otherCharges ?? []).reduce((s, c) => s + (c.amount || 0), 0);
    const totalTax =
      headerTax > 0 ? headerTax : totCgst + totSgst + totIgst;
    const totalInv =
      invoice.grandTotal != null && !Number.isNaN(Number(invoice.grandTotal))
        ? Number(invoice.grandTotal)
        : totTaxable + totalTax + otherAmt;

    return {
      lineRows,
      goodsTotals: {
        totTaxable,
        totCgst,
        totSgst,
        totIgst,
        otherAmt,
        totalTax,
        totalInv,
      },
    };
  }, [invoice, isGst]);

  const inv = invoice as InvoiceHeader & {
    eway_transporter_id?: string;
    eway_transporter_name?: string;
  };
  const transporterId = inv.eway_transporter_id ?? inv.ewayTransporterId ?? '—';
  const transporterName = inv.eway_transporter_name ?? inv.ewayTransporterName ?? '—';
  const distanceKm =
    inv.ewayDistanceKm != null && !Number.isNaN(Number(inv.ewayDistanceKm))
      ? `${inv.ewayDistanceKm} KM`
      : '—';

  const dispatchDate =
    invoice.deliverNoteDate
      ? formatDate(invoice.deliverNoteDate)
      : invoice.invoiceDate
        ? formatDate(invoice.invoiceDate)
        : '—';

  return (
    <div
      className={`eway-bill-doc bg-white text-gray-900 text-sm border border-gray-300 rounded-lg p-6 print:p-2 print:shadow-none print:border-0 print:w-full print:max-w-none print:text-[10px] print:leading-tight ${className}`}
    >
      <div className="border-b border-gray-400 pb-3 mb-4 print:pb-1 print:mb-2">
        <h1 className="text-center text-xl font-bold tracking-wide print:text-base print:mb-0">e-Way Bill</h1>
      </div>

      {/* Table layout: stable in print (avoids md/sm breakpoints collapsing to one column) */}
      <div className="mb-6 border-b border-gray-300 pb-4 overflow-x-auto print:overflow-visible print:mb-2 print:pb-1">
      <table className="eway-bill-meta w-full border-collapse text-sm min-w-[520px] print:min-w-0">
        <tbody>
          <tr className="align-top">
            <td className="font-semibold whitespace-nowrap py-0.5 pr-3 align-top w-[1%] print:py-px">Doc No. :</td>
            <td className="py-0.5 pr-4 align-top print:py-px">{invoice.invoiceNo ?? '—'}</td>
            <td colSpan={2} className="py-0.5 align-top print:py-px text-right whitespace-nowrap">
              <span className="font-semibold">Date :</span>{' '}
              <span>{invoice.invoiceDate ? formatDate(invoice.invoiceDate) : '—'}</span>
            </td>
          </tr>
          <tr className="align-top">
            <td className="font-semibold whitespace-nowrap py-0.5 pr-3 align-top print:py-px">IRN :</td>
            <td colSpan={3} className="py-0.5 break-all align-top print:py-px">
              {invoice.irn ?? '—'}
            </td>
          </tr>
          <tr className="align-top">
            <td className="font-semibold whitespace-nowrap py-0.5 pr-3 align-top print:py-px">Ack No. :</td>
            <td className="py-0.5 pr-4 align-top print:py-px">{invoice.ackNo ?? '—'}</td>
            <td colSpan={2} className="py-0.5 align-top print:py-px text-right whitespace-nowrap">
              <span className="font-semibold">Ack Date :</span>{' '}
              <span>{invoice.ackDate ? formatDate(invoice.ackDate) : '—'}</span>
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      <div className="mb-6 border border-gray-400 p-4 overflow-x-auto print:overflow-visible print:mb-2 print:p-2">
        <h2 className="font-bold text-base mb-3 border-b border-gray-300 pb-2 print:text-xs print:mb-1 print:pb-1">
          1. e-Way Bill Details
        </h2>
        <table className="eway-bill-section1 w-full border-collapse text-sm min-w-[520px] print:min-w-0">
          <tbody>
            <tr className="align-top">
              <td className="font-semibold whitespace-nowrap py-1 pr-3 align-top w-[1%] print:py-px">e-Way Bill No. :</td>
              <td className="py-1 pr-6 md:pr-10 align-top print:py-px">{invoice.ewayBillNo}</td>
              <td className="font-semibold whitespace-nowrap py-1 pr-3 align-top w-[1%] print:py-px">Generated By :</td>
              <td className="py-1 align-top print:py-px">{branchDetails?.gstin ?? '—'}</td>
            </tr>
            <tr className="align-top">
              <td className="font-semibold whitespace-nowrap py-1 pr-3 align-top print:py-px">Supply Type :</td>
              <td className="py-1 pr-6 md:pr-10 align-top print:py-px">Outward</td>
              <td className="font-semibold whitespace-nowrap py-1 pr-3 align-top print:py-px">Mode :</td>
              <td className="py-1 align-top print:py-px">
                {formatTransportMode(invoice.ewayTransportMode, invoice.ewayVehicleType)}
              </td>
            </tr>
            <tr className="align-top">
              <td className="font-semibold whitespace-nowrap py-1 pr-3 align-top print:py-px">Approx Distance :</td>
              <td className="py-1 pr-6 md:pr-10 align-top print:py-px">{distanceKm}</td>
              <td className="font-semibold whitespace-nowrap py-1 pr-3 align-top print:py-px">Transaction Type :</td>
              <td className="py-1 align-top print:py-px">Regular</td>
            </tr>
            <tr className="align-top">
              <td className="font-semibold whitespace-nowrap py-1 pr-3 align-top print:py-px">Generated Date :</td>
              <td className="py-1 pr-6 md:pr-10 align-top print:py-px">{formatEwayWhen(invoice.ewayBillDate)}</td>
              <td className="font-semibold whitespace-nowrap py-1 pr-3 align-top print:py-px">Valid Upto :</td>
              <td className="py-1 align-top print:py-px">{formatEwayWhen(invoice.ewayBillValidTill)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mb-6 border-b border-gray-400 pb-4 print:mb-2 print:pb-1">
        <h2 className="font-bold text-base mb-3 print:text-xs print:mb-1">2. Address Details</h2>
        <table className="eway-bill-address w-full border-collapse text-sm">
          <tbody>
            <tr className="align-top">
              <td className="w-[50%] align-top pr-4 md:pr-8 border-r border-gray-200 print:border-gray-300">
                <p className="font-bold mb-2 print:mb-0.5 print:text-xs">From</p>
                {branchDetails ? (
                  <div className="text-gray-800">
                    <p className="font-medium mb-2 print:mb-0.5 print:text-[10px]">{branchDetails.branchName}</p>
                    <table className="w-full border-collapse">
                      <tbody>
                        <tr className="align-top">
                          <td className="font-semibold whitespace-nowrap py-0.5 pr-2 align-top w-[1%]">GSTIN :</td>
                          <td className="py-0.5 align-top">{branchDetails.gstin ?? '—'}</td>
                        </tr>
                        <tr className="align-top">
                          <td className="font-semibold whitespace-nowrap py-0.5 pr-2 align-top">State :</td>
                          <td className="py-0.5 align-top">{branchDetails.stateName ?? '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="font-semibold mt-2 print:mt-1">Dispatch From :</p>
                    <p className="whitespace-pre-wrap print:text-[10px] leading-snug">{branchDetails.address ?? '—'}</p>
                  </div>
                ) : (
                  <p className="text-gray-500">—</p>
                )}
              </td>
              <td className="w-[50%] align-top pl-4 md:pl-8">
                <p className="font-bold mb-2 print:mb-0.5 print:text-xs">To</p>
                {shipToCustomer ? (
                  <div className="text-gray-800">
                    <p className="font-medium mb-2 print:mb-0.5 print:text-[10px]">{shipToCustomer.customerName}</p>
                    <table className="w-full border-collapse">
                      <tbody>
                        <tr className="align-top">
                          <td className="font-semibold whitespace-nowrap py-0.5 pr-2 align-top w-[1%]">GSTIN :</td>
                          <td className="py-0.5 align-top">{shipToCustomer.gstNo ?? '—'}</td>
                        </tr>
                        <tr className="align-top">
                          <td className="font-semibold whitespace-nowrap py-0.5 pr-2 align-top">State :</td>
                          <td className="py-0.5 align-top">{shipToCustomer.stateName ?? '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="font-semibold mt-2 print:mt-1">Ship To :</p>
                    <p className="whitespace-pre-wrap print:text-[10px] leading-snug">{shipToCustomer.address ?? '—'}</p>
                  </div>
                ) : (
                  <p className="text-gray-500">—</p>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mb-6 border-b border-gray-400 pb-4 print:mb-2 print:pb-1">
        <h2 className="font-bold text-base mb-3 print:text-xs print:mb-1">3. Goods Details</h2>
        <div className="overflow-x-auto border border-gray-300 print:border-gray-400">
          <table className="eway-bill-goods w-full border-collapse text-sm print:text-[9px]">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-300">
                <th className="border border-gray-300 px-2 py-1 text-left font-semibold print:px-1 print:py-px print:text-[9px]">
                  HSN Code
                </th>
                <th className="border border-gray-300 px-2 py-1 text-left font-semibold print:px-1 print:py-px print:text-[9px]">
                  Product Name &amp; Desc.
                </th>
                <th className="border border-gray-300 px-2 py-1 text-right font-semibold print:px-1 print:py-px print:text-[9px]">
                  Quantity
                </th>
                <th className="border border-gray-300 px-2 py-1 text-right font-semibold print:px-1 print:py-px print:text-[9px]">
                  Taxable Amt
                </th>
                <th className="border border-gray-300 px-2 py-1 text-center font-semibold print:px-1 print:py-px print:text-[9px]">
                  Tax Rate (C+S) / IGST
                </th>
              </tr>
            </thead>
            <tbody>
              {lineRows.map((row, idx) => (
                <tr key={idx}>
                  <td className="border border-gray-300 px-2 py-1 print:px-1 print:py-px">{row.hsn}</td>
                  <td className="border border-gray-300 px-2 py-1 text-left print:px-1 print:py-px">{row.desc}</td>
                  <td className="border border-gray-300 px-2 py-1 text-right whitespace-nowrap print:px-1 print:py-px">
                    {row.qty} {row.uom}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-right print:px-1 print:py-px">
                    {row.taxable.toFixed(2)}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-center print:px-1 print:py-px">
                    {row.taxRateLabel}
                  </td>
                </tr>
              ))}
            </tbody>
            {goodsTotals && (
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td
                    colSpan={3}
                    className="border border-gray-300 px-2 py-1 text-right print:px-1 print:py-px"
                  >
                    Tot. Taxable Amt
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-right print:px-1 print:py-px">
                    {goodsTotals.totTaxable.toFixed(2)}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 print:px-1 print:py-px" />
                </tr>
                {!isGst && (
                  <>
                    <tr className="bg-gray-50">
                      <td
                        colSpan={3}
                        className="border border-gray-300 px-2 py-1 text-right font-semibold print:px-1 print:py-px"
                      >
                        CGST Amt
                      </td>
                      <td className="border border-gray-300 px-2 py-1 text-right font-semibold print:px-1 print:py-px">
                        {goodsTotals.totCgst.toFixed(2)}
                      </td>
                      <td className="border border-gray-300 px-2 py-1 print:px-1 print:py-px" />
                    </tr>
                    <tr className="bg-gray-50">
                      <td
                        colSpan={3}
                        className="border border-gray-300 px-2 py-1 text-right font-semibold print:px-1 print:py-px"
                      >
                        SGST Amt
                      </td>
                      <td className="border border-gray-300 px-2 py-1 text-right font-semibold print:px-1 print:py-px">
                        {goodsTotals.totSgst.toFixed(2)}
                      </td>
                      <td className="border border-gray-300 px-2 py-1 print:px-1 print:py-px" />
                    </tr>
                  </>
                )}
                {isGst && (
                  <tr className="bg-gray-50">
                    <td
                      colSpan={3}
                      className="border border-gray-300 px-2 py-1 text-right font-semibold print:px-1 print:py-px"
                    >
                      IGST Amt
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-right font-semibold print:px-1 print:py-px">
                      {goodsTotals.totIgst.toFixed(2)}
                    </td>
                    <td className="border border-gray-300 px-2 py-1 print:px-1 print:py-px" />
                  </tr>
                )}
                <tr className="bg-gray-50">
                  <td
                    colSpan={3}
                    className="border border-gray-300 px-2 py-1 text-right font-semibold print:px-1 print:py-px"
                  >
                    Other Amt
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-right font-semibold print:px-1 print:py-px">
                    {goodsTotals.otherAmt.toFixed(2)}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 print:px-1 print:py-px" />
                </tr>
                <tr className="bg-amber-50 font-bold">
                  <td
                    colSpan={3}
                    className="border border-gray-300 px-2 py-1 text-right print:px-1 print:py-px"
                  >
                    Total Inv Amt
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-right print:px-1 print:py-px" colSpan={2}>
                    {goodsTotals.totalInv.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Single compact block: avoids page-break gap + extra borders between §4 and §5 */}
      <div className="eway-bill-tail border-b border-gray-400 pb-2 mb-0 overflow-x-auto print:overflow-visible print:pb-1 print:mb-0">
        <h2 className="font-bold text-base mb-1 print:text-xs print:mb-0.5">4. Transportation Details</h2>
        <table className="eway-bill-transport w-full border-collapse text-sm min-w-[480px] print:min-w-0">
          <tbody>
            <tr className="align-top">
              <td className="font-semibold whitespace-nowrap py-0.5 pr-2 align-top w-[1%] print:py-px">
                Transporter ID :
              </td>
              <td className="py-0.5 pr-4 md:pr-8 align-top print:py-px">{transporterId}</td>
              <td className="font-semibold whitespace-nowrap py-0.5 pr-2 align-top w-[1%] print:py-px">
                Doc No. :
              </td>
              <td className="py-0.5 align-top print:py-px">{invoice.dispatchDocNo ?? '—'}</td>
            </tr>
            <tr className="align-top">
              <td className="font-semibold whitespace-nowrap py-0.5 pr-2 align-top print:py-px">
                Transporter Name :
              </td>
              <td className="py-0.5 pr-4 md:pr-8 align-top print:py-px">{transporterName}</td>
              <td className="font-semibold whitespace-nowrap py-0.5 pr-2 align-top print:py-px">Date :</td>
              <td className="py-0.5 align-top print:py-px">{dispatchDate}</td>
            </tr>
          </tbody>
        </table>

        <h2 className="font-bold text-base mb-1 mt-2 print:text-xs print:mt-1 print:mb-0.5">5. Vehicle Details</h2>
        <table className="eway-bill-vehicle w-full border-collapse text-sm min-w-[560px] print:min-w-0">
          <tbody>
            <tr className="align-top">
              <td className="font-semibold whitespace-nowrap py-0.5 pr-2 align-top w-[1%] print:py-px">
                Vehicle No. :
              </td>
              <td className="py-0.5 pr-3 md:pr-6 align-top print:py-px">{invoice.motorVehicleNo ?? '—'}</td>
              <td className="font-semibold whitespace-nowrap py-0.5 pr-2 align-top w-[1%] print:py-px">From :</td>
              <td className="py-0.5 pr-3 md:pr-6 align-top print:py-px">
                {invoice.destination ?? branchDetails?.stateName ?? '—'}
              </td>
              <td className="font-semibold whitespace-nowrap py-0.5 pr-2 align-top w-[1%] print:py-px">
                CEWB No. :
              </td>
              <td className="py-0.5 align-top print:py-px">—</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
